import styled from '@emotion/styled';
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import Hls from 'hls.js';
import { useEffect, useRef } from 'react';

type VideoModalType = {
  title: string;
  url: string;
  isHls?: boolean;
};

type VideoModalNavigation = {
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

type VideoModalProps = {
  video: VideoModalType;
  onClose: () => void;
  navigation?: VideoModalNavigation;
};

function isHlsUrl(url: string): boolean {
  return /\.m3u8(\?|$)/i.test(url);
}

const VideoModal = ({ video, onClose, navigation }: VideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const useHls = (video.isHls ?? isHlsUrl(video.url)) && Hls.isSupported();

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (useHls) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(video.url);
      hls.attachMedia(el);
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    el.src = video.url;
    return () => {
      el.removeAttribute('src');
      el.load();
    };
  }, [video.url, useHls]);

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          ×
        </CloseButton>
        <ModalMainColumn>
          <ModalVideoArea>
            <ModalVideoWrapper>
              <video ref={videoRef} controls autoPlay key={video.url} />
            </ModalVideoWrapper>
            {navigation && (
              <ModalNavOverlay>
                <NavCircleButton
                  type="button"
                  aria-label="Previous video"
                  disabled={!navigation.canGoPrevious}
                  onClick={navigation.onPrevious}>
                  <SvgIcon
                    color={tokens.WHITE}
                    width={20}
                    height={20}
                    name={SvgNames.ChevronUp}
                  />
                </NavCircleButton>
                <NavCircleButton
                  type="button"
                  aria-label="Next video"
                  disabled={!navigation.canGoNext}
                  onClick={navigation.onNext}>
                  <StyledDownIcon
                    color={tokens.WHITE}
                    width={20}
                    height={20}
                    name={SvgNames.ChevronDown}
                  />
                </NavCircleButton>
              </ModalNavOverlay>
            )}
          </ModalVideoArea>
          <ModalTitle>
            <Typography size="small">{video.title}</Typography>
          </ModalTitle>
        </ModalMainColumn>
      </ModalContent>
    </ModalBackdrop>
  );
};

const StyledDownIcon = styled(SvgIcon)`
  top: 2px;
  position: relative;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 24px;
  line-height: 1;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const ModalMainColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ModalVideoArea = styled.div`
  position: relative;
  width: 100%;
  background: #000;
`;

const ModalVideoWrapper = styled.div`
  width: 100%;
  background: #000;

  video {
    display: block;
    width: 100%;
    max-height: 70vh;
  }
`;

const ModalNavOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 10px;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
  z-index: 5;

  & > * {
    pointer-events: auto;
  }
`;

const NavCircleButton = styled.button`
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  background: #0f2b6b;
  padding: 0;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35);

  &:hover:not(:disabled) {
    background: #153a8f;
  }

  &:active:not(:disabled) {
    background: #0c2458;
  }

  &:disabled {
    cursor: not-allowed;
    background: #5c6d8a;
    color: rgba(255, 255, 255, 0.82);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }
`;

const ModalTitle = styled.div`
  padding: 12px 16px;
`;

export { VideoModal };
export type { VideoModalType, VideoModalNavigation };
